import { ArrowRightIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { AirlineLogo } from '../../shared/airline_logo/airline_logo.js';
import { fmtTime, fmtWeekdayDayMonth } from '../results/flight_format.js';
import type { FlightSegmentSource } from '../results/flight_offer_source.js';
import { flightNumberLabel, stopCondition } from '../results/offer/flight_leg.js';
import type { FlightFaresCopy } from './fares_copy.js';
import type { FaresLegKind } from './fares_page_shared.js';

/**
 * O voo JÁ ESCOLHIDO daquela perna, no topo da seção de tarifas.
 *
 * **Terceira gramática da perna de voo**, e deliberadamente a mais curta: a
 * `FlightLeg` (recibo) empilha, a `FlightLegDense` (lista) põe o trilho com
 * cidades sob as pontas, e esta cabe numa linha só — aqui o voo não está mais em
 * disputa, ele é o CONTEXTO da escolha que a pessoa veio fazer. Um trilho com
 * nós de escala roubaria a altura das famílias, que são o assunto da tela.
 *
 * A aeronave (`B767-3` no frame) NÃO entra: nem o modal-session nem o provider a
 * carregam, e a única fonte é um lookup do Cirium por perna que só resolve em
 * trecho direto (mesma conclusão da #2391). Ela segue no drawer "Detalhes".
 */
export function SelectedFlightRow({
  kind,
  seg,
  stops,
  readOnly,
  onDetail,
  onSeats,
  onChangeFlight,
  copy,
  locale,
}: {
  readonly kind: FaresLegKind;
  readonly seg: FlightSegmentSource;
  readonly stops: number;
  readonly readOnly?: boolean;
  readonly onDetail: () => void;
  /** "Assentos" é a outra porta do MESMO drawer. Ausente em trecho com escala:
   *  o mapa é pedido por identidade de PERNA, e conexão não tem uma só (mesma
   *  regra da linha de ofertas). */
  readonly onSeats?: () => void;
  /** Volta à lista daquela perna. Ausente = congelado (checkout em cena). */
  readonly onChangeFlight?: () => void;
  readonly copy: FlightFaresCopy;
  /** O idioma das datas. */
  readonly locale: string;
}) {
  const leg = copy.offer.leg;
  const kindWord = leg.kind[kind];
  return (
    <div className="ffs-flight">
      <span className="ffs-flight-logo">
        <AirlineLogo
          {...(seg.carrier ? { iata: seg.carrier } : {})}
          {...(seg.carrierName ? { name: seg.carrierName } : {})}
          size={22}
        />
      </span>
      <span className="ffs-flight-main">
        {/* Sentido · data · janela de horários: a identificação que a pessoa
            reconhece do card que ela acabou de escolher, na mesma ordem. */}
        <span className="ffs-flight-title">
          {`${kindWord} · ${fmtWeekdayDayMonth(seg.departAt, locale)} · ${fmtTime(seg.departAt)} – ${fmtTime(seg.arriveAt)}`}
        </span>
        <span className="ffs-flight-sub">
          <span className="ffs-flight-no">{flightNumberLabel(seg.carrier, seg.flightNumber)}</span>
          {/* `Detalhes` ANTES de `Assentos`, ao contrário da linha da lista
              (`Assentos · Detalhes`, #2363). A inversão é do frame desta tela, e
              tem razão: aqui o voo já está escolhido, então a porta que se abre
              com mais frequência é a de conferir o que se escolheu — o mapa de
              assentos é a exceção. Se o designer quiser uma ordem só nas duas
              telas, é ESTA que muda: lá a ordem é convenção da lista inteira. */}
          <button type="button" className="ffs-link" onClick={onDetail}>
            {leg.viewDetail}
          </button>
          {onSeats && (
            <button type="button" className="ffs-link" onClick={onSeats}>
              {copy.offer.seats}
            </button>
          )}
        </span>
      </span>
      <span className="ffs-flight-trip">
        {seg.durationLabel && <span className="ffs-flight-dur">{seg.durationLabel}</span>}
        <span className="ffs-flight-route">
          {seg.from}
          <ArrowRightIcon size={12} aria-hidden />
          {seg.to}
        </span>
      </span>
      <span className="ffs-flight-cond">{stopCondition(stops, leg)}</span>
      {onChangeFlight && (
        // O rótulo VISÍVEL é "Alterar voo" nas duas seções; o nome acessível diz
        // QUAL voo, porque fora do contexto visual dois botões idênticos na mesma
        // página não se distinguem numa lista de controles.
        // `VOLL/Button` outline: o nó do frame é um `<instance name="VOLL/Button">`
        // de 87x28, e `size="sm"` É esse degrau (h-7). O pill bespoke daqui media
        // 34px — 6px mais alto que o desenho. A classe sobrevive porque ela ainda
        // POSICIONA (a media query estreita move o botão de célula no grid da
        // linha); o que ela não faz mais é desenhar.
        <Button
          variant="outline"
          size="sm"
          className="ffs-change rounded-full"
          aria-label={copy.changeFlightAria(kindWord.toLowerCase())}
          onClick={onChangeFlight}
          disabled={readOnly}
        >
          {copy.changeFlight}
        </Button>
      )}
    </div>
  );
}
